import React, { useState } from "react";
import * as XLSX from "xlsx";
import API_BASE from "../config/api";

/*
  CR U8 ("bulk upload of listings under the free-listing plan") and
  U9 ("upload an Excel sheet once to add multiple businesses in one go").
  Parses the sheet entirely in the browser (SheetJS), shows a preview,
  then posts the parsed rows to bulk_submit_listings.php in one request.
*/

const TEMPLATE_COLUMNS = [
    "title", "catagory", "mobile", "email", "province", "district", "muncipality",
    "ward", "addresses", "descriptions", "website", "keywords",
];

const TEMPLATE_SAMPLE_ROW = {
    title: "Sample Cafe", catagory: "Restaurant", mobile: "9800000000", email: "owner@example.com",
    province: "Bagmati", district: "Kathmandu", muncipality: "Kathmandu Metropolitan City", ward: 5,
    addresses: "New Road, Kathmandu", descriptions: "Cozy cafe serving coffee and snacks.",
    website: "", keywords: "cafe, coffee, kathmandu",
};

export default function BulkListingUpload({ personaluid }) {
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState("");
    const [parseError, setParseError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [results, setResults] = useState(null);

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([TEMPLATE_SAMPLE_ROW], { header: TEMPLATE_COLUMNS });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Listings");
        XLSX.writeFile(wb, "dialnepal-bulk-listing-template.xlsx");
    };

    const handleFile = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;

        setParseError("");
        setResults(null);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: "binary" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                if (parsed.length === 0) {
                    setParseError("This sheet has no rows.");
                    setRows([]);
                    return;
                }
                if (parsed.length > 200) {
                    setParseError(`This sheet has ${parsed.length} rows — please split it into batches of 200 or fewer.`);
                    setRows([]);
                    return;
                }
                setRows(parsed);
            } catch {
                setParseError("Could not read this file. Make sure it's a valid .xlsx or .csv file.");
                setRows([]);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleUpload = async () => {
        setUploading(true);
        setResults(null);
        try {
            const res = await fetch(`${API_BASE}/api/bulk_submit_listings.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ personaluid, rows }),
            });
            const data = await res.json();
            setUploading(false);
            if (data.success) {
                setResults(data);
                setRows([]);
                setFileName("");
            } else {
                setParseError(data.message || "Upload failed.");
            }
        } catch {
            setUploading(false);
            setParseError("Network error while uploading.");
        }
    };

    return (
        <div>
            <h5 className="fw-bold mb-1">Bulk Upload Listings</h5>
            <p className="text-secondary small mb-3">
                Add multiple businesses at once under the free listing plan. Download the template, fill it in, then upload it here.
                Every listing goes in as <b>Free plan, pending review</b> — same as adding one manually.
            </p>

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={downloadTemplate}>
                            <i className="bi bi-download me-1" /> Download Excel Template
                        </button>
                        <label className="btn btn-outline-primary btn-sm mb-0">
                            <i className="bi bi-upload me-1" /> Choose File
                            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFile} />
                        </label>
                        {fileName && <span className="text-secondary small">{fileName} — {rows.length} row(s) found</span>}
                    </div>

                    {parseError && <div className="alert alert-danger py-2">{parseError}</div>}

                    {rows.length > 0 && (
                        <>
                            <div className="table-responsive mb-3" style={{ maxHeight: 300 }}>
                                <table className="table table-sm">
                                    <thead className="table-light">
                                        <tr>{TEMPLATE_COLUMNS.map((c) => <th key={c}>{c}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, 20).map((r, i) => (
                                            <tr key={i}>{TEMPLATE_COLUMNS.map((c) => <td key={c}>{String(r[c] ?? "")}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {rows.length > 20 && <p className="text-secondary small">Showing first 20 of {rows.length} rows.</p>}
                            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                                {uploading ? "Uploading..." : `Import ${rows.length} Listing${rows.length === 1 ? "" : "s"}`}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {results && (
                <div className="card shadow-sm">
                    <div className="card-header bg-light fw-semibold">
                        Import Result — {results.imported} of {results.total} succeeded
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="table-light"><tr><th>Row</th><th>Title</th><th>Status</th></tr></thead>
                            <tbody>
                                {results.results.map((r) => (
                                    <tr key={r.row}>
                                        <td>{r.row}</td>
                                        <td>{r.title || "—"}</td>
                                        <td>
                                            {r.success
                                                ? <span className="badge bg-success">Imported</span>
                                                : <span className="badge bg-danger" title={r.message}>Failed — {r.message}</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
